import { store } from 'redux/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n/config';
import { translate } from 'hooks/useTranslate';

import { HoldingClass, Partner, TradingOffer } from 'types';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { getTradingOffer } from '../api/getTradingOffer';
import { getTradingOffers } from '../api/getTradingOffers';
import { isShareClassVisible } from '../utils/isShareClassVisible';

type UseGetTradingOfferOptionsProps = {
  societyId?: string;
  tradingOfferId?: string;
  items?: number;
};

const TRADING_OFFER_TYPE_LABEL_KEYS: Record<string, string> = {
  SELL: 'TradingOfferTypeSell',
  BUY: 'TradingOfferTypeBuy',
};

export const useGetTradingOfferOptions = ({
  societyId,
  tradingOfferId,
  items,
}: UseGetTradingOfferOptionsProps) => {
  const [language, setLanguage] = useState(i18n.language);
  const [selectedTradingOffer, setSelectedTradingOffer] =
    useState<TradingOffer | null>(null);
  const [tradingOffers, setTradingOffers] = useState<TradingOffer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const mapOption = useCallback(
    (tradingOffer: TradingOffer): CustomAsyncSelectOption => {
      const showShareClass = isShareClassVisible();

      const parts = [
        (tradingOffer?.partner as Partner)?.name || '',
        translate(
          TRADING_OFFER_TYPE_LABEL_KEYS[tradingOffer?.type] ||
            tradingOffer?.type
        ),
        `${tradingOffer?.sharesCount?.total} ${translate('Shares')}`,
        showShareClass
          ? (tradingOffer?.shareClass as HoldingClass)?.name
          : null,
      ].filter(Boolean);

      return {
        value: tradingOffer?._id,
        label: parts.join(' · '),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );
  const selectedTradingOfferOption = useMemo(
    () => (selectedTradingOffer ? mapOption(selectedTradingOffer) : null),
    [selectedTradingOffer, mapOption]
  );

  const tradingOfferOptions = useMemo(
    () => tradingOffers.map(mapOption),
    [tradingOffers, mapOption]
  );

  const handleChangeTradingOfferOption = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    const found =
      tradingOffers.find((offer) => offer._id === selectedOption?.value) ||
      null;
    setSelectedTradingOffer(found);
  };

  const fetchTradingOffers = useCallback(
    async (controller: AbortController) => {
      if (!societyId) return;
      setIsLoading(true);
      try {
        if (tradingOfferId) {
          const tradingOffer = await store.dispatch(
            getTradingOffer({ tradingOfferId })
          );
          setSelectedTradingOffer(tradingOffer);
          setIsLoading(false);
          return;
        }

        const { data } = await store.dispatch(
          getTradingOffers({
            societyId,
            params: { items: items || 10 },
            signal: controller.signal,
          })
        );

        if (!controller.signal.aborted) {
          setTradingOffers(data);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    [items, societyId, tradingOfferId]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchTradingOffers(controller);

    return () => controller.abort();
  }, [fetchTradingOffers]);

  const handleSearchTradingOffer = async (searchTerm: string) => {
    if (!searchTerm || !societyId) return undefined;

    try {
      setIsLoading(true);

      const { data } = await store.dispatch(
        getTradingOffers({ societyId, params: { searchTerm } })
      );

      setIsLoading(false);
      return data.map(mapOption);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
      return undefined;
    }
  };

  return {
    data: {
      selectedTradingOfferOption,
      tradingOfferOptions,
    },
    isLoading,
    isError,
    actions: {
      handleChangeTradingOfferOption,
      handleSearchTradingOffer,
    },
  };
};
